/* components/SiteHeader.ts
Global Site Header

Example
<site-header>
    Site header content here
</site-header>
*/

import { AnnouncementBar } from '../AnnouncementBar/AnnouncementBar';
import { CartMain } from "../Cart/CartMain";
import { Component, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import debounce from 'lodash/debounce';
import { SideCart } from '../SideCart/SideCart';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { DrawerContainer } from '../Drawer/DrawerContainer';
import { DrawerHamburger } from '../Drawer/DrawerHamburger';
import { cart } from '@/theme/Cart';

@Component
export class SiteHeader extends ThemeComponent {
    @Ref() row!: HTMLDivElement;
    @Ref({required: false}) cartTrigger!: HTMLDivElement;
    @Ref() menuToggle!: DrawerHamburger[];
    @Ref({required: false}) count!: HTMLElement;
    @Ref({ required: false }) announcement!: AnnouncementBar;
    @Ref mobileMenu!: DrawerContainer;
    @Ref searchTrigger !: HTMLButtonElement;
    @Ref searchForm!: HTMLInputElement;
    @Ref mobileSearchTrigger!: HTMLButtonElement;

    private heroObserver: IntersectionObserver | null = null;

    private _onOpenChange = new SimpleEvent<string>();
    private sideCart:SideCart|null = document.body.querySelector('side-cart');

    public get onOpenChange() {
        return this._onOpenChange.asEvent();
    }

    public changeOpen(open: string) {
        // Use the private backing field to dispatch
        this._onOpenChange.dispatch('');
    }

    init() {
        this.handleBodyPadding();
        this.setupHeroObserver();
        this.setEventListeners();
        this.handleSearchBar();
        this.listenForOuterDrawerClick();

        const cartMain = document.body.querySelector('cart-main') as CartMain;
        document.addEventListener('cartChanged', (e: Event) => {
            this.updateCartCount();
        });
    }

    /**
     * Set event listeners.
     */
    setEventListeners() {
        this.$listen(window, 'resize',
            debounce(() => {
                this.handleBodyPadding();
                this.setupHeroObserver();
            }, 300)
        );

        this.handleScroll();
        this.cartEvent();
    }

    /**
     * Handle Body Paddin
     */

    private handleSearchBar() {
        this.$listen(this.searchTrigger, 'click', () => {
            document.dispatchEvent(new Event('searchOpen'));
        });

        this.$listen(this.mobileSearchTrigger, 'click', () => {
            document.dispatchEvent(new Event('searchOpen'));
        });

        // document.addEventListener('searchOpen', () => {
        //     document.body.classList.add('search-open');
        //     this.searchForm.focus();
        //     document.dispatchEvent(new Event('hamburgerOpen'));
        // });

        // document.addEventListener('searchClose', () => {
        //     document.body.classList.remove('search-open');
        //     document.dispatchEvent(new Event('hamburgerClose'));
        // });
    }

    public listenForOuterDrawerClick() {
        this.$listen(this, 'click', (e) => {
            if (e.target) {
                const clickedElement = e.target as HTMLElement;
                if (clickedElement === this) {
                    document.body.classList.remove('drawer-open','search-open');
                    document.dispatchEvent(new Event('closeMenu'));
                    document.dispatchEvent(new Event('hamburgerClose'));
                }
            }
        });
    }

    private getFirstVisibleHeroSection(): HTMLElement | null {
        const sections = document.querySelectorAll('#MainContent > .shopify-section');
        for (const section of Array.from(sections)) {
            const el = section as HTMLElement;
            if (el.offsetHeight === 0) {
                continue;
            }
            const isHero = el.classList.contains('hero__wrapper') ||
                !!el.querySelector('.hero-general, .hero-secondary, .hero-app');
            if (isHero) {
                return el;
            }
        }
        return null;
    }

    private handleBodyPadding() {
        const isTransparent = this.classList.contains('site-header__wrapper--transparent');
        const heroSection = isTransparent ? this.getFirstVisibleHeroSection() : null;

        if (heroSection) {
            document.documentElement.style.paddingTop = '0px';
            return;
        }

        const height = this.parentElement?.offsetHeight;
        document.documentElement.style.paddingTop = `${height}px`;
    }

    private setupHeroObserver() {
        this.heroObserver?.disconnect();

        const header = this.closest('header') as HTMLElement | null;
        if (!header) { return; }

        const isTransparent = this.classList.contains('site-header__wrapper--transparent');
        const heroSection = isTransparent ? this.getFirstVisibleHeroSection() : null;

        if (!heroSection) {
            header.classList.remove('site-header--over-hero', 'site-header--past-hero');
            return;
        }

        header.classList.add('site-header--over-hero');
        header.classList.remove('site-header--past-hero');

        const headerHeight = header.offsetHeight;

        this.heroObserver = new IntersectionObserver(
            ([entry]) => {
                header.classList.toggle('site-header--past-hero', !entry.isIntersecting);
            },
            {
                rootMargin: `-${headerHeight}px 0px 0px 0px`,
                threshold: 0
            }
        );

        this.heroObserver.observe(heroSection);
    }

    private handleScroll() {
        const header = this.closest('header');
        let prevScrollY = window.pageYOffset || document.documentElement.scrollTop;

        window.addEventListener('scroll', () => {
            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
            const headerHeight = this.offsetHeight;

            if (currentScrollY > headerHeight && currentScrollY < prevScrollY) {
                header?.classList.add('site-header--up');
                header?.classList.remove('site-header--down');
            } else if (currentScrollY > headerHeight && currentScrollY > prevScrollY) {
                header?.classList.add('site-header--down');
                header?.classList.remove('site-header--up');
            } else {
                header?.classList.remove('site-header--up');
                header?.classList.remove('site-header--down');
            }

            prevScrollY = currentScrollY;
        });
    }

    /**
     * Cart Close click event.
     * @returns
     */
    cartEvent() {
        if (!this.cartTrigger) { return; }
        this.$listen(this.cartTrigger, 'click', (event) => {
            event.preventDefault();

            if (!this.sideCart) { return; }
            this.sideCart.open();
        });
    }

    /**
     * Update cart count
     * @returns
     */
    private updateCartCount() {
        const count = cart.getCartItemCount();
        this.count.innerHTML = String(count);
        if (count > 0) {
            this.count.parentElement?.classList.add('is-active');
        } else {
            this.count.parentElement?.classList.remove('is-active');
        }
    }
}
